// The shell for world surfaces (Universe, Discover, Marketplace, Learn,
// Reflections, Settings). The studio and Stage are fullscreen and shell-free.

import { Link, NavLink, Outlet } from "react-router-dom";
import { Toggle } from "@/ui/Toggle";
import { useUi } from "@/state/ui";
import { COMPANY_ONLY_PAGES, LEGAL_PAGES } from "@/company/pages";
import { AccountControl } from "./AccountControl";
import "./shell.css";

const NAV = [
  { to: "/universe", label: "Universe" },
  { to: "/discover", label: "Discover" },
  { to: "/marketplace", label: "Marketplace" },
  { to: "/learn", label: "Learn" },
  { to: "/reflections", label: "Reflections" },
] as const;

export function AppShell() {
  const { theme, setTheme } = useUi();
  return (
    <div className="bd-shell">
      <header className="bd-shell__bar">
        <NavLink to="/" className="bd-shell__mark" aria-label="Beat Draft home">
          <Wordmark />
        </NavLink>
        <nav className="bd-shell__nav" aria-label="Main">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) => `bd-shell__link${isActive ? " is-active" : ""}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="bd-shell__end">
          <Toggle
            checked={theme === "light"}
            onChange={(on) => setTheme(on ? "light" : "dark")}
            label="Light theme"
            hideLabel
          />
          <NavLink to="/settings" className="bd-shell__link">
            Settings
          </NavLink>
          <AccountControl />
        </div>
      </header>
      <main className="bd-shell__main">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

function Footer() {
  return (
    <footer className="bd-footer">
      <div className="bd-footer__inner">
        <div className="bd-footer__brand">
          <Wordmark size={16} />
          <p>Music should never be finished. It should evolve.</p>
        </div>
        <nav className="bd-footer__col" aria-label="Product">
          <h4>Product</h4>
          <Link to="/universe">Universe</Link>
          <Link to="/discover">Discover</Link>
          <Link to="/marketplace">Marketplace</Link>
          <Link to="/learn">Learn</Link>
          <Link to="/blog">Blog</Link>
        </nav>
        <nav className="bd-footer__col" aria-label="Company">
          <h4>Company</h4>
          {COMPANY_ONLY_PAGES.map((p) => (
            <Link key={p.slug} to={`/company/${p.slug}`}>
              {p.title.replace("Beat Draft", "").replace("About", "About").trim() || p.title}
            </Link>
          ))}
          <Link to="/status">Status</Link>
        </nav>
        <nav className="bd-footer__col" aria-label="Legal">
          <h4>Legal</h4>
          {LEGAL_PAGES.map((p) => (
            <Link key={p.slug} to={`/legal/${p.slug}`}>
              {p.title}
            </Link>
          ))}
        </nav>
      </div>
      <div className="bd-footer__base">
        <span>Beat Draft. The home of living music.</span>
        <Link to="/legal/cookies">Cookie preferences</Link>
      </div>
    </footer>
  );
}

/** The logotype: minimal, all-caps, BEAT bold + DRAFT regular. Typography is
 *  the identity; no calligraphy, no colored tile. `size` sets the cap height. */
export function Wordmark({ size = 14 }: { size?: number }) {
  return (
    <span className="bd-wordmark" style={{ fontSize: size }} aria-label="Beat Draft">
      <span className="bd-wordmark__beat">Beat</span>
      <span className="bd-wordmark__draft">Draft</span>
    </span>
  );
}
